import { useState } from 'react';
import { XMarkIcon, CheckCircleIcon, XCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';

interface PressRegistration {
  id: string;
  full_name: string;
  email: string;
  phone: string;
  media_outlet: string;
  media_type: string;
  status: string;
  created_at: string;
  accreditation_type?: string;
  country?: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  registration: PressRegistration | null;
}

function statusColor(status: string) {
  switch (status) {
    case 'approved': return 'bg-green-100 text-green-700';
    case 'rejected': return 'bg-red-100 text-red-700';
    case 'pending': return 'bg-amber-100 text-amber-700';
    default: return 'bg-gray-100 text-gray-500';
  }
}

export default function MobilePressDetailsSheet({ isOpen, onClose, registration }: Props) {
  const [updating, setUpdating] = useState(false);

  if (!isOpen || !registration) return null;

  const handleStatusChange = async (newStatus: string) => {
    try {
      setUpdating(true);
      const { error } = await supabase
        .from('press_registrations')
        .update({ status: newStatus })
        .eq('id', registration.id);
      if (error) throw error;
      toast.success(`Status updated to ${newStatus}`);
      onClose();
    } catch {
      toast.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const row = (label: string, value: string | undefined) =>
    value ? (
      <div className="flex items-start justify-between gap-4 py-2.5 border-b border-gray-50 last:border-0">
        <p className="text-xs text-gray-400 flex-shrink-0">{label}</p>
        <p className="text-xs font-medium text-golf-gray text-right break-words max-w-[60%]">{value}</p>
      </div>
    ) : null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-t-3xl shadow-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-base font-bold text-golf-gray">Press Registration</h2>
            <p className="text-xs text-gray-400 mt-0.5">{registration.full_name}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 active:bg-gray-200 transition-colors">
            <XMarkIcon className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-bold text-golf-gray">{registration.full_name}</p>
              <p className="text-sm text-gray-500">{registration.media_outlet}</p>
            </div>
            <span className={`text-xs px-2.5 py-1 rounded-full font-medium capitalize ${statusColor(registration.status)}`}>
              {registration.status}
            </span>
          </div>

          <div className="bg-gray-50 rounded-xl px-4">
            {row('Email', registration.email)}
            {row('Phone', registration.phone)}
            {row('Media Type', registration.media_type)}
            {row('Accreditation', registration.accreditation_type)}
            {row('Country', registration.country)}
            {row('Registered', new Date(registration.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }))}
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Update Status</p>
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => handleStatusChange('approved')}
                disabled={updating || registration.status === 'approved'}
                className="flex flex-col items-center gap-1.5 py-3 rounded-xl bg-green-50 border border-green-200 text-green-700 active:bg-green-100 disabled:opacity-40 transition-colors"
              >
                <CheckCircleIcon className="w-5 h-5" />
                <span className="text-xs font-semibold">Approve</span>
              </button>
              <button
                onClick={() => handleStatusChange('pending')}
                disabled={updating || registration.status === 'pending'}
                className="flex flex-col items-center gap-1.5 py-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 active:bg-amber-100 disabled:opacity-40 transition-colors"
              >
                <ClockIcon className="w-5 h-5" />
                <span className="text-xs font-semibold">Pending</span>
              </button>
              <button
                onClick={() => handleStatusChange('rejected')}
                disabled={updating || registration.status === 'rejected'}
                className="flex flex-col items-center gap-1.5 py-3 rounded-xl bg-red-50 border border-red-200 text-red-500 active:bg-red-100 disabled:opacity-40 transition-colors"
              >
                <XCircleIcon className="w-5 h-5" />
                <span className="text-xs font-semibold">Reject</span>
              </button>
            </div>
          </div>

          <div className="pb-2" />
        </div>

        <div className="px-5 py-4 border-t border-gray-100 flex-shrink-0">
          <button onClick={onClose} className="w-full py-3 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 bg-white active:bg-gray-50 transition-colors">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
